import { __decorate, __metadata, __param } from "tslib";
import { E_ACTIVITY_OBJECT, E_ACTIVITY_OBJECT_REQUIRED, productNameKey } from '@/apis/service/ApplyTrialApi.type';
import { Symbols } from '@/symbols';
import { getDateFormatYMD } from '@/utils/date';
import { Mandarin } from 'flatpickr/dist/l10n/zh';
import { inject, injectable } from 'inversify';
import _ from 'lodash';
export function makeSelfRegisterLinkForm(data = {}) {
    const { product_pojo_list, ...otherData } = data;
    const package_keys = _.map(product_pojo_list || [], m => m.package_key || m.product_id);
    const mrData = {
        ...otherData,
        package_keys,
        promotion_packages: _.map(_.get(data, 'promotion_packages', []), m => m.package_id || m.product_id),
    };
    if (data.trial_days) {
        mrData.trial_days = data.trial_days.toString();
    }
    const defaultData = {
        title: '',
        slogan: '',
        activity_begin_at: null,
        activity_end_at: null,
        activity_object: '',
        campaign_id: null,
        package_key: [],
        trial_begin_at: null,
        trial_end_at: '',
        trial_days: '7',
        create_sales_lead: null,
        channel_source: '',
        channel_code: '',
        campaign_promotion: '',
        lead_source: '',
        promotion_plan: '',
        promotion_cell: '',
        media_type: '',
        take_effect_now: true,
        mobile_number_required: E_ACTIVITY_OBJECT_REQUIRED.REQUIRED,
        email_required: E_ACTIVITY_OBJECT_REQUIRED.REQUIRED,
        company_name_required: E_ACTIVITY_OBJECT_REQUIRED.REQUIRED,
        nickname_required: E_ACTIVITY_OBJECT_REQUIRED.DISPLAY,
        job_role_required: E_ACTIVITY_OBJECT_REQUIRED.DISPLAY,
        firstname_required: E_ACTIVITY_OBJECT_REQUIRED.DISPLAY,
        lastname_required: E_ACTIVITY_OBJECT_REQUIRED.DISPLAY,
        sales_email: null,
        promotion_packages: [],
    };
    const formData = _.merge(defaultData, mrData);
    formData.trial_begin_at = getDateFormatYMD(formData.trial_begin_at || new Date());
    formData.activity_begin_at = formData.activity_begin_at && getDateFormatYMD(formData.activity_begin_at);
    formData.activity_end_at = formData.activity_end_at && getDateFormatYMD(formData.activity_end_at);
    return formData;
}
let SelfRegisterLinkService = class SelfRegisterLinkService {
    constructor(i18n, applyTrialApi, systemService, packagesService) {
        this.i18n = i18n;
        this.applyTrialApi = applyTrialApi;
        this.systemService = systemService;
        this.packagesService = packagesService;
        // #region 已启用的套餐
        this._enabledPackages = [];
        this.getActivityPromotionPagination = this.applyTrialApi.getActivityPromotionPagination;
        this.getActivityPromotion = this.applyTrialApi.getActivityPromotion;
        this.getActivityPromotionProductPackageKey = this.applyTrialApi.getActivityPromotionProductPackageKey;
        this.patchActivityPromotionEnabled = this.applyTrialApi.patchActivityPromotionEnabled;
        this.postSyncCampaigns = this.applyTrialApi.postSyncCampaigns;
        this.postActivityPromotionBackground = this.applyTrialApi.postActivityPromotionBackground;
    }
    get ActivityObjectOption() {
        return [
            {
                text: this.i18n.t('backoffice.operationPromotionLink.E_ACTIVITY_OBJECT.new'),
                value: E_ACTIVITY_OBJECT.new,
                tip: this.i18n.t('backoffice.operationPromotionLink.E_ACTIVITY_OBJECT.newTip'),
            },
            {
                text: this.i18n.t('backoffice.operationPromotionLink.E_ACTIVITY_OBJECT.all'),
                value: E_ACTIVITY_OBJECT.all,
                tip: this.i18n.t('backoffice.operationPromotionLink.E_ACTIVITY_OBJECT.allTip'),
            }
        ];
    }
    get TakeEffectiveNowOption() {
        return [
            { text: this.i18n.t('backoffice.operationPromotionLink.effectiveImmediately'), value: true, tip: this.i18n.t('backoffice.operationPromotionLink.effectiveImmediatelyTip') },
            { text: this.i18n.t('backoffice.operationPromotionLink.customStartDate'), value: false, tip: this.i18n.t('backoffice.operationPromotionLink.customStartDateTip') }
        ];
    }
    get CreateSalesLead() {
        return [
            { text: this.i18n.t('backoffice.operationPromotionLink.NotCreateSalesLead'), value: false },
            {
                text: this.systemService.IsCn ? this.i18n.t('backoffice.operationPromotionLink.CreateSalesLeadForCn') : this.i18n.t('backoffice.operationPromotionLink.CreateSalesLeadForOther'),
                value: true,
            }
        ];
    }
    get isCnLocale() {
        return this.i18n.locale === 'cn';
    }
    get FlatPickrConfig() {
        const option = {
            locale: this.isCnLocale ? Mandarin : null,
            defaultDate: getDateFormatYMD(new Date()),
            minDate: getDateFormatYMD(new Date()),
            dateFormat: 'Y-m-d',
        };
        return option;
    }
    get enabledPackages() {
        return this._enabledPackages;
    }
    initEnabledPackages() {
        return this.packagesService.getEnabledPackages().then(res => {
            this._enabledPackages = res;
        });
    }
    // #endregion
    get enabledPackagesMap() {
        return _.keyBy(this._enabledPackages, item => _.trim(item.package_key).toLowerCase());
    }
    getEnabledPackageNameByKey(packageKey) {
        const item = this.enabledPackagesMap[packageKey.toLowerCase()];
        return (this.isCnLocale ? item?.package_name : item?.en_name) || packageKey;
    }
    getProductNames(product) {
        return this.i18n.t(productNameKey[product.product_id]);
    }
    getProductPackageName(product) {
        if (product.package_key) {
            return this.getEnabledPackageNameByKey(product.package_key);
        }
        return this.getProductNames(product);
    }
    getPromotionPackageName(product) {
        return this.isCnLocale ? _.get(product, 'name_cn') : _.get(product, 'name_us');
    }
    getActivityPromotionParams(params) {
        const { firstname_required, lastname_required, nickname_required, mobile_number_required, ...other } = params;
        if (this.systemService.IsCn) {
            return { nickname_required, mobile_number_required, ...other };
        }
        else {
            return { firstname_required, lastname_required, ...other };
        }
    }
    postActivityPromotion(params) {
        const data = this.getActivityPromotionParams(params);
        return this.applyTrialApi.postActivityPromotion(data);
    }
    putActivityPromotion(params) {
        const data = this.getActivityPromotionParams(params);
        return this.applyTrialApi.putActivityPromotion(data);
    }
};
SelfRegisterLinkService = __decorate([
    injectable(),
    __param(0, inject(Symbols.I18n)),
    __param(1, inject(Symbols.ApplyTrialApi)),
    __param(2, inject(Symbols.SystemService)),
    __param(3, inject(Symbols.PackagesService)),
    __metadata("design:paramtypes", [Object, Object, Object, Object])
], SelfRegisterLinkService);
export { SelfRegisterLinkService };
